<script setup lang="ts">

import { computed, useSlots, toRef, ref, watch } from 'vue'
import { useField } from 'vee-validate'
import { id as id2 } from 'random-html-id'
import { modes } from '@/composables/useInteractionMode'

export interface Props {
  label?: string
  required?: boolean
  type?: string
  name: string
  id?: string
  error?: string,
  autocomplete?: string
  prefix?: string
  suffix?: string
  help?: string
  placeholder?: string
  prefixClass?: string
  suffixClass?: string
  mode?: String,
  autofocus?: boolean
  readonly?: boolean
  disabled?: boolean
  rows?: number
  rules?: string
  tooltip?: string
  maxlength?: number
  inputClass?: string
}

const props = withDefaults(defineProps<Props>(), {
  label: '',
  required: false,
  maxlength: 524288,
  type: 'text',
  error: '',
  mode: 'aggressive',
  autocomplete: 'off',
  prefix: '',
  id: '',
  suffix: '',
  help: '',
  placeholder: '',
  prefixClass: 'pl-10',
  suffixClass: 'pr-10',
  autofocus: false,
  readonly: false,
  disabled: false,
  rules: '',
  tooltip: '',
  rows: 5,
  inputClass: ''
})

const name = toRef(props, 'name')
const rules = toRef(props, 'rules')
const label = toRef(props, 'label')

const { value, errorMessage, meta, handleChange, handleBlur } = useField(name, rules, { label, validateOnValueUpdate: false })
const htmlId = computed(() => props.id ? props.id : `input-${id2()}`)
const hasError = computed(() => meta.touched && errorMessage.value !== undefined)
const $slots = useSlots()
const hasPrefix = computed(() => !!$slots.prefix || props.prefix)
const hasSuffix = computed(() => !!$slots.suffix || props.suffix)

const emit = defineEmits(['click', 'input'])
const inputRef = ref<HTMLElement | null>(null)

defineExpose({
  inputRef
})

watch(value, (val) => {
  emit('input', val)
})

const handlers = computed(() => {
  const on = {
    blur: handleBlur,
    // default input event to sync the value
    // the `false` here prevents validation
    input: [(e) => handleChange(e, false)]
  }

  // Get list of validation events based on the current mode
  const triggers = modes[props.mode]({
    errorMessage,
    meta
  })

  // add them to the "on" handlers object
  triggers.forEach((t) => {
    if (Array.isArray(on[t])) {
      on[t].push(handleChange)
    } else {
      on[t] = handleChange
    }
  })

  return on
})

</script>

<template>
  <div>
    <template v-if="label">
      <div class="flex-1 flex items-center">
        <div class="flex-1">
          <slot name="label">
            <storm-ui-label
              v-if="label"
              :label="label"
              :required="rules?.includes('required')"
            />
          </slot>
        </div>
      </div>
    </template>
    <template v-if="type == 'textarea'">
      <textarea
        :id="htmlId"
        ref="textarea"
        v-model="value"
        :disabled="disabled"
        class="w-full rounded-md  border-gray-300 text-base font-normal text-gray-800 focus:ring-2 form-textarea resize-none border px-1.5 focus:border-blue-400 focus:outline-none focus:ring-blue-200 placeholder-gray-400"
        :class="[disabled ? 'bg-gray-50' : '', inputClass]"
        :rows="rows"
        :maxlength="maxlength"
        :name="name"
        :readonly="readonly"
      />
    </template>
    <template v-else>
      <div class="w-full relative">
        <div
          v-if="hasPrefix || prefix"
          class="flex border absolute items-center pointer-events-none inset-y-0 left-0 border-transparent pl-2"
        >
          <span
            class="text-base text-gray-500"
          >
            <slot name="prefix">
              {{ prefix }}
            </slot>
          </span>
        </div>
        <input
          :id="htmlId"
          ref="inputRef"
          :value="value"
          class="w-full px-2 py-2 h-9 border-gray-300 text-base focus:border-blue-400 focus:ring-blue-200 text-black block rounded font-sans disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 focus:ring focus:ring-opacity-50 placeholder:text-gray-400"
          :class="
            [
              'font-sans',
              disabled ? 'bg-gray-50' : '',
              hasPrefix ? prefixClass : '',
              hasSuffix ? suffixClass : '',
              hasError ? ' focus:border-red-400 bg-red-50  focus:ring-red-200 border-red-300' : 'border-gray-300 focus:border-blue-400  focus:ring-blue-200',
              inputClass
            ]
          "
          :disabled="disabled"
          :autocomplete="autocomplete"
          :autofocus="autofocus"
          :placeholder="placeholder"
          :name="name"
          :type="type"
          :maxlength="maxlength"
          :readonly="readonly"
          v-on="handlers"
          @click="emit('click')"
        >
        <div
          v-if="hasSuffix || suffix"
          class="absolute inset-y-0 flex items-center border border-transparent right-0 pr-2"
        >
          <slot
            name="suffix"
          >
            <span class="text-base text-gray-500">
              {{ suffix }}
            </span>
          </slot>
        </div>
      </div>
    </template>
    <div
      class="flex text-xs item-center pt-1.5"
    >
      <div
        class="font-normal flex-1 text-gray-400"
      >
        <slot>{{ help }}</slot>
      </div>
    </div>
  </div>
</template>
